// PPS CRM - Messages Module
import { useEffect, useState } from 'react';
import { api } from '../lib/api';

interface Thread {
  id: string;
  subject: string;
  updatedAt: string;
  messages: { id: string; body: string; createdAt: string; senderId: string }[];
  participants: { user: { id: string; fullLegalName: string; role: string } }[];
}

export default function Messages() {
  const [threads, setThreads] = useState<Thread[]>([]);
  const [activeThread, setActiveThread] = useState<Thread | null>(null);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);

  useEffect(() => {
    fetchThreads();
  }, []);

  const fetchThreads = async () => {
    try {
      const response = await api.get('/api/messages/threads');
      setThreads(response.data);
      if (!activeThread && response.data.length > 0) {
        setActiveThread(response.data[0]);
      }
    } catch (error) {
      console.error('Error fetching threads:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSelect = (thread: Thread) => setActiveThread(thread);

  return (
    <div className="flex gap-6">
      <div className="w-80 card h-full">
        <div className="flex justify-between items-center mb-4">
          <h3 className="font-semibold">Threads</h3>
          <button
            onClick={() => setShowModal(true)}
            className="text-sm text-primary hover:underline"
          >
            New
          </button>
        </div>
        {loading ? (
          <div className="text-center">Loading...</div>
        ) : threads.length === 0 ? (
          <div className="text-center text-dark-gray">No threads</div>
        ) : (
          <div className="space-y-2">
            {threads.map((thread) => (
              <button
                key={thread.id}
                onClick={() => handleSelect(thread)}
                className={`w-full text-left p-3 rounded ${
                  activeThread?.id === thread.id ? 'bg-primary-light' : 'bg-light-gray'
                }`}
              >
                <div className="font-medium">{thread.subject}</div>
                <div className="text-xs text-dark-gray">
                  {new Date(thread.updatedAt).toLocaleString()}
                </div>
              </button>
            ))}
          </div>
        )}
      </div>

      <div className="flex-1 card">
        {!activeThread ? (
          <div className="text-center text-dark-gray">Select a thread</div>
        ) : (
          <ThreadView thread={activeThread} onReplied={fetchThreads} />
        )}
      </div>

      {showModal && (
        <NewThreadModal
          onClose={() => setShowModal(false)}
          onSave={() => {
            setShowModal(false);
            fetchThreads();
          }}
        />
      )}
    </div>
  );
}

function ThreadView({ thread, onReplied }: { thread: Thread; onReplied: () => void }) {
  const [message, setMessage] = useState('');
  const [sending, setSending] = useState(false);

  const handleReply = async () => {
    if (!message.trim()) return;
    setSending(true);
    try {
      await api.post(`/api/messages/thread/${thread.id}/reply`, { body: message });
      setMessage('');
      onReplied();
    } catch (error) {
      console.error('Error replying:', error);
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="flex flex-col h-full">
      <div className="border-b border-light-gray pb-3 mb-3">
        <h3 className="font-semibold">{thread.subject}</h3>
        <div className="text-sm text-dark-gray">
          Participants: {thread.participants.map((p) => p.user.fullLegalName).join(', ')}
        </div>
      </div>
      <div className="flex-1 space-y-3 overflow-y-auto">
        {thread.messages.map((m) => (
          <div key={m.id} className="p-3 bg-light-gray rounded">
            <div className="text-sm">{m.body}</div>
            <div className="text-xs text-mid-gray mt-1">{new Date(m.createdAt).toLocaleString()}</div>
          </div>
        ))}
      </div>
      <div className="mt-4 flex gap-2">
        <input
          type="text"
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          className="flex-1 px-3 py-2 border border-mid-gray rounded"
          placeholder="Type a reply..."
        />
        <button
          onClick={handleReply}
          disabled={sending}
          className="bg-primary text-white px-4 py-2 rounded hover:bg-primary-dark disabled:opacity-50"
        >
          Send
        </button>
      </div>
    </div>
  );
}

function NewThreadModal({ onClose, onSave }: { onClose: () => void; onSave: () => void }) {
  const [subject, setSubject] = useState('');
  const [participants, setParticipants] = useState('');
  const [body, setBody] = useState('');
  const [saving, setSaving] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    try {
      const ids = participants
        .split(',')
        .map((s) => s.trim())
        .filter(Boolean);
      await api.post('/api/messages/thread', { subject, participants: ids, body });
      onSave();
    } catch (error) {
      console.error('Error creating thread:', error);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 w-full max-w-lg">
        <h3 className="text-xl font-bold mb-4">New Thread</h3>
        <form onSubmit={handleSubmit}>
          <div className="mb-4">
            <label className="block text-sm font-medium mb-1">Subject</label>
            <input
              type="text"
              required
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              className="w-full px-3 py-2 border border-mid-gray rounded"
            />
          </div>
          <div className="mb-4">
            <label className="block text-sm font-medium mb-1">Participants (User IDs, comma-separated)</label>
            <input
              type="text"
              value={participants}
              onChange={(e) => setParticipants(e.target.value)}
              className="w-full px-3 py-2 border border-mid-gray rounded"
            />
          </div>
          <div className="mb-4">
            <label className="block text-sm font-medium mb-1">Message</label>
            <textarea
              value={body}
              onChange={(e) => setBody(e.target.value)}
              rows={3}
              className="w-full px-3 py-2 border border-mid-gray rounded"
            />
          </div>
          <div className="flex justify-end gap-2 mt-6">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 border border-mid-gray rounded hover:bg-light-gray"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving}
              className="px-4 py-2 bg-primary text-white rounded hover:bg-primary-dark disabled:opacity-50"
            >
              {saving ? 'Saving...' : 'Create'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
